import { Card, Alert, Divider, Button, Typography, Row, Col, Space, message } from "antd";
import { Icon } from "Components";
import { MappingItem, TenantMapping } from "../Mapping";
import { confirmMapping } from "Services/Api/PciCompliance/Mapping.Service";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

interface MappingWizardStep4Props {
  tenantInfo: TenantMapping;
  fileName: string;
  mappings: MappingItem[];
  onConfirm: (count: number) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  onBack: () => void;
}

export function MappingWizardStep4({
  tenantInfo,
  fileName,
  mappings,
  onConfirm,
  onBack,
  setIsSubmitting,
}: MappingWizardStep4Props) {

  const { t } = useTranslation();

  const validMappings = mappings.filter(
    m => !!m.encytroOffice && !!m.venzaProperty && m.status !== "removed"
  );
  const removedCount = mappings.filter(m => m.status === "removed").length;
  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const result = await confirmMapping(
        tenantInfo,
        [...mappings.filter(m => m.status !== "removed").map((c) => ({
          property_id: c?.venzaPropertyId ?? "",
          property_name: c.venzaProperty ?? "",
          mid: c.encytroMid,
        }))],
      );

      message.success(t("mapping.errors.successMapping2"));
      console.log("API response:", result);
      onConfirm(mappings.filter(m => m.status !== "removed").length);

    } catch (error: any) {
      console.error("Error:", error);

      if (error.status === 404) {
        const errorMessage =
          error.data?.errors?.[0] ||
          error.data?.message ||
          t("mapping.errors.validationFailed");
        message.error(errorMessage);
      } else if (error.status === 422) {
        const errorMessage =
          error.data?.errors?.[0] ||
          error.data?.message ||
          t("mapping.errors.validationFailed");

        message.error(errorMessage);
      } else {
        message.error(t("mapping.errors.somethingWrong"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
    <Card>
      <div className="mbx-16">
        <Title level={3} className="headerTitle">{t("mapping.createMapping.step4.title")}</Title>
        <p className="headerSubtitle">{t("mapping.createMapping.step4.sub")}</p>
      </div>
      <Alert
        type="success"
        showIcon
        className="alert-success mbx-24"
        icon={<Icon type="alertSuccess" />}
        message={t("mapping.createMapping.step4.alert")}
      />

      {/* Mapping Summary */}
      <div className="mbx-16">
        <Title level={4} className="mbx-12 fw-400 fs-1 card-foreground">
          {t("mapping.createMapping.step4.summary.title")}
        </Title>
        <Space direction="vertical" className="w-100" size="middle">
          <Card className="mappingSummary">
            <Space>
              <Icon type="mappingSummary" />
              <div>
                <Text type="secondary" className="text-muted-foreground">
                  {t("mapping.createMapping.step4.summary.venzaTenant")}
                </Text>
                <div className="fs-1 card-foreground">{tenantInfo.name}</div>
              </div>
            </Space>
          </Card>
          <Card className="mappingSummary">
            <Space>
              <Icon type="tenantFile" />
              <div>
                <Text type="secondary" className="text-muted-foreground">
                  {t("mapping.createMapping.step4.summary.sourceFile")}
                </Text>
                <div className="fs-1 card-foreground">{fileName}</div>
              </div>
            </Space>
          </Card>
        </Space>
      </div>

      <Divider />

      <p className="sectionTitle">{t("mapping.createMapping.step4.stats.title")}</p>

      <div className="grid-button text-center">
        <Card className="statusCard totalMappings" bodyStyle={{ padding: 16 }}>
          <div className="cardHeader justify-center">
            <p>{t("mapping.createMapping.step4.stats.total")}</p>
          </div>
          <p className="cardValue">{validMappings.length}</p>
        </Card>

        <Card className="statusCard successfulMappings" bodyStyle={{ padding: 16 }}>
          <div className="cardHeader justify-center">
            <p>{t("mapping.createMapping.step4.stats.success")}</p>
          </div>
          <p className="alertSuccess">{validMappings.length}</p>
        </Card>

        <Card
          className="statusCard ignoredMappingsCard"
          hoverable
          bodyStyle={{ padding: 16 }}
        >
          <div className="cardHeader justify-center">
            <p>{t("mapping.createMapping.step4.stats.removed")}</p>
          </div>
          <p className="cardValue">{removedCount}</p>
        </Card>
      </div>

      <Divider />

      {/* Mapped Properties */}
      <div className="mbx-24">
        <p className="sectionTitle">
          {t("mapping.createMapping.step4.mappedProps.title")} ({validMappings.length})
        </p>
        <div className="mappedPropertiesList">
          {validMappings.map((mapping) => (
            <Row
              key={mapping.id}
              className="mappedPropertyRow"
              justify="space-between"
              align="middle"
            >
              <Col xs={24} sm={12}>
                <Text className="text-muted-foreground">
                  {t("mapping.createMapping.step4.mappedProps.venza")}
                </Text>
                <p className="card-foreground fs-1 mb-0">
                  {mapping?.venzaProperty}
                </p>
              </Col>
              <Col xs={24} sm={12}>
                <Text className="text-muted-foreground">
                  {t("mapping.createMapping.step4.mappedProps.pci")}
                </Text>
                <p className="card-foreground fs-1 mb-0">
                  {mapping?.encytroOffice}
                </p>
              </Col>
            </Row>
          ))}
        </div>
      </div>

      {/* Buttons */}
      <div className="footerButtons">
        <Button className="backButton" onClick={onBack}>
          {t("mapping.createMapping.step4.btn.back")}
        </Button>
        <Button
          className="continueButton"
          type="primary"
          disabled={mappings.length === 0}
          onClick={handleConfirm}
        >
          <Icon type="confirmation" className="mrx-8" />
          <span>{t("mapping.createMapping.step4.btn.confirm")}</span>
        </Button>
      </div>
    </Card>
  );

}
